import api, { handleResponse, handleError } from './api';

const profileService = {
  /**
   * Get the current user's profile
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async getProfile() {
    try {
      const response = await api.get('/profiles/me');
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Create a new profile for the current user
   * @param {object} data - Profile data
   * @param {string} data.firstName - First name
   * @param {string} data.lastName - Last name
   * @param {string} [data.bio] - Biography
   * @param {string} [data.instagramHandle] - Instagram handle
   * @param {string} [data.twitterHandle] - Twitter handle
   * @param {string} [data.linkedInUrl] - LinkedIn URL
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async createProfile(data) {
    try {
      const response = await api.post('/profiles', data);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Update the current user's profile
   * @param {object} data - Profile data to update
   * @param {string} [data.firstName] - First name
   * @param {string} [data.lastName] - Last name
   * @param {string} [data.bio] - Biography
   * @param {string} [data.instagramHandle] - Instagram handle
   * @param {string} [data.twitterHandle] - Twitter handle
   * @param {string} [data.linkedInUrl] - LinkedIn URL
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async updateProfile(data) {
    try {
      const response = await api.put('/profiles/me', data);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Upload an avatar image for the current user's profile
   * @param {File} file - The image file to upload
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async uploadAvatar(file) {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/profiles/me/avatar', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Get a profile by User ID (not Profile ID)
   * @param {string} userId - User entity ID
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async getProfileByUserId(userId) {
    try {
      const response = await api.get(`/profiles/user/${userId}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Get a profile by ID
   * @param {string} id - Profile ID
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async getProfileById(id) {
    try {
      const response = await api.get(`/profiles/${id}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Get a profile by handle (username)
   * @param {string} handle - Profile handle
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async getProfileByHandle(handle) {
    try {
      const response = await api.get(`/profiles/handle/${handle}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Check if a handle is available
   * @param {string} handle - The handle to check
   * @returns {Promise<{success: boolean, data: {available: boolean, message: string|null}|null, error: string|null}>}
   */
  async checkHandle(handle) {
    try {
      const response = await api.get(`/profiles/check-handle?handle=${encodeURIComponent(handle)}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Search for profiles by name or handle
   * @param {string} query - Search query
   * @param {number} [limit=20] - Max results to return
   * @returns {Promise<{success: boolean, data: object[]|null, error: string|null}>}
   */
  async searchProfiles(query, limit = 20) {
    try {
      const response = await api.get(`/profiles/search?q=${encodeURIComponent(query)}&limit=${limit}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  // === Gallery Methods ===

  /**
   * Upload a photo to the gallery (max 6 photos)
   * @param {File} file - The image file to upload
   * @param {string} [caption] - Optional caption for the photo
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async uploadGalleryPhoto(file, caption = null) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (caption) {
        formData.append('caption', caption);
      }

      const response = await api.post('/profiles/me/gallery', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Delete a gallery photo
   * @param {string} photoId - The ID of the photo to delete
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async deleteGalleryPhoto(photoId) {
    try {
      const response = await api.delete(`/profiles/me/gallery/${photoId}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Reorder gallery photos
   * @param {string[]} photoIds - Array of photo IDs in the desired order
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async reorderGalleryPhotos(photoIds) {
    try {
      const response = await api.put('/profiles/me/gallery/reorder', { photoIds });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Update a gallery photo caption
   * @param {string} photoId - The ID of the photo
   * @param {string|null} caption - The new caption (or null to remove)
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async updateGalleryPhotoCaption(photoId, caption) {
    try {
      const response = await api.put(`/profiles/me/gallery/${photoId}`, { caption });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },
};

export default profileService;
